import {
  DisconnectReason,
  type Participant,
  type RemoteParticipant,
  type RemoteTrack,
  Room,
  RoomEvent,
  Track,
} from "livekit-client";

import type { VoiceInfo, VoiceKind, VoiceMessage } from "../types";

/** Folga para parar de ouvir quem passa do raio (nao fica liga-desliga na borda). */
const HEAR_HYSTERESIS = 32;
/** Espera antes de pedir um token novo quando a conexao com o LiveKit cai. */
const RETRY_MS = 2500;
/** Captura do microfone (a mesma faixa passa de sala em sala). */
const MIC_OPTIONS = { echoCancellation: true, noiseSuppression: true, autoGainControl: true };

export type VoiceStatus = "connecting" | "connected" | "error";

/** O que o painel de voz mostra. */
export interface VoiceState {
  status: VoiceStatus;
  kind: VoiceKind;
  label: string;
  /** o microfone esta ligado agora */
  mic: boolean;
  /** o navegador bloqueou o som ate o jogador tocar na pagina */
  needsGesture: boolean;
  /** outras pessoas na mesma sala ou conversa (na area aberta nao conta) */
  peers: number;
}

export interface VoiceChatOptions {
  info: VoiceInfo;
  /** O hub manda a sala de novo com um token novo. */
  requestToken: () => void;
  onState: (state: VoiceState) => void;
  /** Ids de quem esta falando agora (o proprio jogador incluso). */
  onSpeaking: (ids: Set<string>) => void;
  onNotice: (text: string, level: "info" | "warn" | "error") => void;
}

/** Posicao de um jogador para a regra de proximidade. */
export interface VoicePeer {
  x: number;
  y: number;
  /** esta numa conversa trancada (fica em outra sala do LiveKit) */
  locked: boolean;
}

/**
 * O lado do navegador do chat de voz. O servidor do jogo decide em que sala
 * do LiveKit o jogador fica (join recebe essa decisao); aqui so conectamos,
 * ligamos o microfone e, na area aberta, assinamos e regulamos o volume de
 * cada pessoa pela distancia (updateProximity). Nas salas e nas conversas
 * trancadas todo mundo se ouve com volume cheio.
 */
export class VoiceChat {
  private room: Room | null = null;
  private current: VoiceMessage | null = null;
  /** Cada join incrementa: um connect antigo que termina depois e descartado. */
  private seq = 0;
  private status: VoiceStatus = "connecting";
  /** O jogador quer o microfone ligado (vale para as proximas salas tambem). */
  private micWanted = false;
  private micOn = false;
  /**
   * A captura do microfone, reaproveitada de sala em sala: pedir o microfone
   * ao navegador de novo a cada troca deixava a pessoa muda por ~2 s. So a
   * captura passa adiante; a faixa do LiveKit e nova em cada sala (a antiga
   * fica presa a conexao que fechou).
   */
  private micCapture: MediaStreamTrack | null = null;
  private needsGesture = false;
  private retryTimer: number | null = null;
  private destroyed = false;
  private speaking = new Set<string>();
  private readonly unlockAudio = () => void this.room?.startAudio().catch(() => undefined);

  constructor(private readonly opts: VoiceChatOptions) {
    // O navegador so toca audio depois de um gesto do jogador na pagina.
    window.addEventListener("pointerdown", this.unlockAudio, true);
    window.addEventListener("keydown", this.unlockAudio, true);
  }

  get micEnabled(): boolean {
    return this.micOn;
  }

  /** Entra na sala que o servidor mandou (troca de sala desconecta a anterior). */
  async join(msg: VoiceMessage): Promise<void> {
    if (this.destroyed) return;
    const seq = ++this.seq;
    this.clearRetry();
    this.current = msg;
    this.status = "connecting";
    this.micOn = false;
    this.setSpeaking(new Set());
    this.emit();

    const previous = this.room;
    this.room = null;
    // false: a faixa do microfone sobrevive para ir para a sala nova
    if (previous) await previous.disconnect(false).catch(() => undefined);
    if (seq !== this.seq || this.destroyed) return;

    const room = new Room({
      // so audio: nada de camera, simulcast nem ajuste de video
      adaptiveStream: false,
      dynacast: false,
      // o volume por pessoa passa por um GainNode (setVolume aceita > 1)
      webAudioMix: true,
      audioCaptureDefaults: MIC_OPTIONS,
      disconnectOnPageLeave: true,
    });
    this.room = room;
    this.wire(room);

    try {
      // na area aberta quem decide o que assinar e a distancia (updateProximity)
      await room.connect(msg.url || sameOriginURL(), msg.token, { autoSubscribe: msg.kind !== "open" });
    } catch (error) {
      if (seq !== this.seq || this.destroyed) return;
      console.warn("chat de voz: falha ao conectar", error);
      this.room = null;
      this.status = "error";
      this.emit();
      this.scheduleRetry();
      return;
    }
    if (seq !== this.seq || this.destroyed) {
      void room.disconnect();
      return;
    }
    this.status = "connected";
    this.needsGesture = !room.canPlaybackAudio;
    this.emit();
    if (this.micWanted) await this.applyMic(true);
  }

  /** Liga ou desliga o microfone (a escolha vale tambem para as proximas salas). */
  async setMic(on: boolean): Promise<void> {
    this.micWanted = on;
    this.unlockAudio();
    if (this.room && this.status === "connected") await this.applyMic(on);
    else this.emit();
  }

  toggleMic(): void {
    void this.setMic(!this.micWanted);
  }

  private async applyMic(on: boolean): Promise<void> {
    const room = this.room;
    if (!room) return;
    try {
      if (on) {
        // derrubado pelo servidor, o LiveKit para a captura: ai pede de novo
        if (this.micCapture?.readyState === "ended") this.releaseMic();
        if (!this.micCapture) {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: MIC_OPTIONS });
          this.micCapture = stream.getAudioTracks()[0] ?? null;
        }
        if (room !== this.room || !this.micCapture) return;
        await room.localParticipant.publishTrack(this.micCapture, { source: Track.Source.Microphone });
      } else {
        const pub = room.localParticipant.getTrackPublication(Track.Source.Microphone);
        if (pub?.track) await room.localParticipant.unpublishTrack(pub.track, false);
        this.releaseMic();
      }
      if (room !== this.room) return;
      this.micOn = on;
    } catch (error) {
      console.warn("chat de voz: microfone", error);
      this.micWanted = false;
      this.micOn = false;
      this.releaseMic();
      this.opts.onNotice(micErrorMessage(error), "error");
    }
    this.emit();
  }

  /** Desliga o microfone de vez (a luz de gravacao do navegador apaga). */
  private releaseMic(): void {
    this.micCapture?.stop();
    this.micCapture = null;
  }

  /**
   * Area aberta: assina quem esta a ate hearRadius (com folga para sair) e
   * regula o volume pela distancia. Chamado algumas vezes por segundo.
   */
  updateProximity(me: { x: number; y: number }, peers: Map<string, VoicePeer>): void {
    const room = this.room;
    if (!room || this.status !== "connected" || this.current?.kind !== "open") return;
    const { hearRadius, fullRadius } = this.opts.info;
    for (const participant of room.remoteParticipants.values()) {
      const peer = peers.get(participant.identity);
      const distance = peer && !peer.locked ? Math.hypot(peer.x - me.x, peer.y - me.y) : Infinity;
      // o volume vai antes da assinatura: a faixa ja entra no volume certo
      participant.setVolume(volumeAt(distance, fullRadius, hearRadius));
      for (const pub of participant.audioTrackPublications.values()) {
        const hear = distance <= hearRadius || (pub.isSubscribed && distance <= hearRadius + HEAR_HYSTERESIS);
        if (pub.isSubscribed !== hear) pub.setSubscribed(hear);
      }
    }
  }

  private wire(room: Room): void {
    room
      .on(RoomEvent.TrackSubscribed, (track: RemoteTrack, _pub, participant: RemoteParticipant) => {
        if (track.kind !== Track.Kind.Audio) return;
        const el = track.attach();
        el.dataset.voice = participant.identity;
        document.body.append(el);
        if (this.current?.kind !== "open") participant.setVolume(1);
      })
      .on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
        for (const el of track.detach()) el.remove();
      })
      .on(RoomEvent.ActiveSpeakersChanged, (speakers: Participant[]) => {
        if (room !== this.room) return;
        // na area aberta so conta quem o jogador esta ouvindo (e ele mesmo)
        const ids = new Set<string>();
        for (const p of speakers) {
          const heard =
            p === room.localParticipant ||
            this.current?.kind !== "open" ||
            [...p.audioTrackPublications.values()].some((pub) => pub.isSubscribed);
          if (heard) ids.add(p.identity);
        }
        this.setSpeaking(ids);
      })
      .on(RoomEvent.ParticipantConnected, () => this.emit())
      .on(RoomEvent.ParticipantDisconnected, () => this.emit())
      .on(RoomEvent.AudioPlaybackStatusChanged, () => {
        this.needsGesture = !room.canPlaybackAudio;
        this.emit();
      })
      .on(RoomEvent.Disconnected, (reason?: DisconnectReason) => {
        if (room !== this.room || this.destroyed) return;
        this.room = null;
        this.micOn = false;
        this.setSpeaking(new Set());
        if (reason === DisconnectReason.DUPLICATE_IDENTITY) {
          // a conta entrou na voz por outra aba: esta para de tentar
          this.status = "error";
          this.emit();
          return;
        }
        // tirado pela moderacao/varredura, servidor reiniciou, rede caiu:
        // o hub decide de novo em que sala ficar
        this.status = "connecting";
        this.emit();
        this.scheduleRetry();
      });
  }

  private scheduleRetry(): void {
    this.clearRetry();
    this.retryTimer = window.setTimeout(() => {
      this.retryTimer = null;
      if (!this.destroyed) this.opts.requestToken();
    }, RETRY_MS);
  }

  private clearRetry(): void {
    if (this.retryTimer !== null) window.clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }

  private setSpeaking(ids: Set<string>): void {
    if (ids.size === this.speaking.size && [...ids].every((id) => this.speaking.has(id))) return;
    this.speaking = ids;
    this.opts.onSpeaking(ids);
  }

  private emit(): void {
    if (this.destroyed) return;
    const msg = this.current;
    this.opts.onState({
      status: this.status,
      kind: msg?.kind ?? "open",
      label: msg?.label ?? "",
      mic: this.micOn,
      needsGesture: this.needsGesture,
      peers: this.room?.remoteParticipants.size ?? 0,
    });
  }

  destroy(): void {
    this.destroyed = true;
    this.seq++;
    this.clearRetry();
    window.removeEventListener("pointerdown", this.unlockAudio, true);
    window.removeEventListener("keydown", this.unlockAudio, true);
    void this.room?.disconnect();
    this.room = null;
    this.releaseMic();
    for (const el of document.querySelectorAll("audio[data-voice]")) el.remove();
  }
}

/** Volume cheio ate fullRadius, caindo ate zero em hearRadius. */
export function volumeAt(distance: number, fullRadius: number, hearRadius: number): number {
  if (distance <= fullRadius) return 1;
  if (distance >= hearRadius) return 0;
  const t = (distance - fullRadius) / (hearRadius - fullRadius);
  return Math.pow(1 - t, 1.5);
}

/** O LiveKit no mesmo endereco do jogo: o nginx (e o Vite no dev) encaminha /rtc. */
function sameOriginURL(): string {
  return `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}`;
}

function micErrorMessage(error: unknown): string {
  const name = error instanceof Error ? error.name : "";
  if (name === "NotAllowedError" || name === "SecurityError") {
    return "O navegador não liberou o microfone. Permita o acesso no cadeado da barra de endereço e tente de novo.";
  }
  if (name === "NotFoundError" || name === "OverconstrainedError") {
    return "Nenhum microfone encontrado neste aparelho.";
  }
  if (name === "NotReadableError") {
    return "O microfone está em uso por outro programa.";
  }
  return "Não foi possível ligar o microfone.";
}
